import { useMemo } from "react";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable"
import { ColDef, IServerSideDatasource, ValueFormatterParams } from "ag-grid-community";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { bookingsFilterState } from "../../../search/recoil";
import { useRceDashboardCounterMutation } from "../../api/getRceDashboardCounter";
import { useRceDashboardListMutation } from "../../api/getRceDashboardList";
import Chip from "@mui/material/Chip";
import CellActions from "./cell-actions";
import { AutorenewRounded, BuildRounded, CloudSyncRounded, FastForwardRounded, HealthAndSafety, KeyOff, MoveDownRounded, StarRounded, StoreRounded } from "@mui/icons-material";
import Stack from "@mui/material/Stack";
import { VipType } from "../../../../types";
import { depositStatus, depositType, RaStatus } from "../../types";
import CellStatus from "../cell-status/CellStatus";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import { ISortBy } from "../../../../api/types";

function DashboardTable() {
    const { t } = useTranslation('booking');
    const bookingsFilter = useRecoilValue(bookingsFilterState);

    function valToEur(val: any) {
        val = String(val);
        while (val.length < 3) {
            val = "0" + val;
        }

        const start: string = val.substring(0, val.length - 2);
        const end: string = val.substring(val.length - 2);

        return "€ " + start + "." + end;
    }

    const columnDefs = useMemo<ColDef<any>[]>(() => [
        {
            field: 'tags',
            headerName: t('label.tags'),
            pinned: 'left',
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: any) => {
                return (
                    <Stack direction="row" alignItems="center">
                        {params.data && [VipType.Internal, VipType.External, VipType.VipCS].includes(params.data.vip) &&
                            <StarRounded
                                titleAccess={t('label.vip')}
                                color="secondary"
                            />
                        }
                        {params.data?.fastLine &&
                            <FastForwardRounded
                                titleAccess={t('label.fastLine')}
                                color="secondary"
                            />
                        }
                        {params.data?.carSwap &&
                            <CloudSyncRounded
                                titleAccess={t('label.carSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.substitution &&
                            <BuildRounded
                                titleAccess={t('label.substitution')}
                                color="secondary"
                            />
                        }
                        {params.data?.customerAtDesk &&
                            <StoreRounded
                                titleAccess={t('label.customerAtDesk')}
                                color="secondary"
                            />
                        }
                        {params.data?.renewal &&
                            <AutorenewRounded
                                titleAccess={t('label.renewal')}
                                color="secondary"
                            />
                        }
                        {params.data?.recovery &&
                            <HealthAndSafety
                                titleAccess={t('label.recovery')}
                                color="secondary"
                            />
                        }
                        {params.data?.packageSwap &&
                            <MoveDownRounded
                                titleAccess={t('label.packageSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.keyless &&
                            <KeyOff
                                titleAccess={t('label.keyless')}
                                color="secondary"
                            />
                        }
                    </Stack>
                );
            }
        },
        {
            field: 'id',
            headerName: t("label.bookingNumber"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
        },
        {
            field: 'createdDate',
            headerName: t("label.creationDate"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
        },
        {
            field: 'prepaymentStatus',
            headerName: t("label.paymentStatus"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            cellRenderer(params: any) {
                let color;
                switch (params.value) {
                    case "SETTLED":
                        color = "success";
                        break;
                    case undefined:
                        color = "success";
                        break;
                    default:
                        color = "warning";
                }

                return (
                    <Tooltip title={params.value ? t(`tooltip.rce_dashboard.${params.value}`) : t('tooltip.rce_dashboard.NOT_SETTLED')}>
                        <Chip
                            sx={{ paddingLeft: "4px", paddingRight: "4px" }}
                            color={color as any}
                            size="small"
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: 'depositStatus',
            headerName: t("label.depositStatus"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            cellRenderer(params: any) {
                let color;
                switch (params.value as depositStatus) {
                    case "SETTLED":
                        color = "success";
                        break;
                    case "REJECTED":
                        color = "error";
                        break;
                    case "NOT_YET_SETTLED":
                    default:
                        color = "warning";
                }

                return (
                    <Tooltip title={params.value ? t(`tooltip.rce_dashboard.${params.value}`) : t('tooltip.rce_dashboard.NOT_YET_SETTLED')}>
                        <Chip
                            sx={{ paddingLeft: "4px", paddingRight: "4px" }}
                            color={color as any}
                            size="small"
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: 'depositType',
            headerName: t("label.depositType"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            cellRenderer: (params: any) => {
                switch (params.value as depositType) {
                    case "BANK_TRASFER":
                        return t("label.rce_dashboard.deposit_type.bank_transfer");
                    case "PREAUTHORIZATION":
                    default:
                        return t("label.rce_dashboard.deposit_type.preauth");
                }
            }
        },
        {
            field: 'raStatus',
            headerName: t("label.raStatus"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            cellRenderer(params: any) {
                let color;
                switch (params.value as RaStatus) {
                    case "READY_TO_SIGN":
                        color = "warning";
                        break;
                    case "READY_SIGNED":
                    case "ONGOING":
                        color = "success";
                        break;
                    default:
                        color = null;
                }

                return color && (
                    <Tooltip title={params.value ? t(`status.${params.value}`) : t('status.NOT_CREATED')}>
                        <Chip
                            sx={{ paddingLeft: "4px", paddingRight: "4px" }}
                            color={color as any}
                            size="small"
                        />
                    </Tooltip>
                )
            }
        },
        {
            field: 'depositAmount',
            headerName: t("label.depositAmount"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true,
            sortable: true,
            valueGetter: (params) => {
                return (params.data?.depositType as depositType == "BANK_TRASFER") ? valToEur(params.data?.depositAmount) : valToEur(params.data?.preautAmount)
            }
        },
        {
            field: 'mainDriver',
            headerName: t("label.mainDriver"),
            minWidth: 200,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true
        },
        {
            field: 'statusSubstatus',
            headerName: "",
            minWidth: 240,
            pinned: 'right',
            suppressMovable: true,
            resizable: false,
            suppressColumnsToolPanel: true,
            cellRenderer: CellStatus
        },
        {
            field: "_actions",
            headerName: "",
            resizable: false,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            pinned: 'right',
            cellRenderer: CellActions,
        },
    ], [t])

    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: false,
        unSortIcon: true,
        minWidth: 130,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        // headerComponent: DashboardHeaderCol,
        // cellRendererSelector: (params: any) => {
        //     if (params.node.rowPinned) {
        //         return {
        //             component: PinnedRowRender,
        //             params: {
        //                 field: ['location'],
        //                 style: { 'fontWeight': 'bold' },
        //             }
        //         };
        //     } else {
        //         // rows that are not pinned don't use any cell renderer
        //         return undefined;
        //     }
        // }
    }), []);

    const counterMutation = useRceDashboardCounterMutation();
    const listMutation = useRceDashboardListMutation();

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            const counterRes = await counterMutation.mutateAsync({ filters: bookingsFilter });
            const counter = parseInt(counterRes);
            if (Number.isNaN(counter) || !counter) {
                return params.success({
                    rowCount: 0,
                    rowData: [],
                })
            }


            const start = params.request.startRow;
            const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined)
                ? params.request.endRow - params.request.startRow
                : undefined;

            const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                field: params.request.sortModel[0].colId,
                order: params.request.sortModel[0].sort
            } : undefined;

            const listRes = await listMutation.mutateAsync({
                filters: bookingsFilter,
                sortBy: sortBy,
                start: start,
                limit: limit,
            });
            params.success({
                rowCount: counter,
                rowData: listRes.rows.map((r: any) => r.booking),
            })
        }
    }), [bookingsFilter])

    return (
        <AgGridCustomTable
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            serverSideDatasource={serverSideDatasource}
            paginationPageSize={25}
            rowModelType="serverSide"
            // pinnedBottomRowData={[pinnedBottomRowData]}
            // defaultColGroupDef={defaultColGroupDef}
            suppressMultiSort
            suppressDragLeaveHidesColumns
            cacheBlockSize={25}
            enableCellTextSelection
            tooltipShowDelay={0}
        />
    )
}

export default DashboardTable
